import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {BrandLogo} from '@salesforce/retail-react-app/app/components/icons';
import {Box, Center, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerOverlay, IconButton, Spinner, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useIntl} from 'react-intl';

import {HeaderCloseIcon, LeftArrowIcon} from '@amblique/king-living/app/components/icons';
import Search from '@amblique/king-living/app/components/search';
import {NavigationPrimaryTabs} from '@amblique/king-living/app/components/header/partials/navigation-primary-tabs';
import {MegaMenuPanel} from '@amblique/king-living/app/components/mega-menu/mobile/mega-menu-panel';
import {MegaMenuSubcategoryPanel} from '@amblique/king-living/app/components/mega-menu/mobile/mega-menu-subcategory-panel';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {getPromotionalLinks} from '@amblique/king-living/app/utils/navigation-primary-tabs';
import {lockBodyScrollFreeze, unlockBodyScrollFreeze} from '@amblique/king-living/app/utils/utils';
import {NAV_MOBILE_L2_SLIDE_MS, SEARCH_TAB_ID} from '@amblique/king-living/app/constants/navigation-drawer';

const getMobileL2SlideMs = () => {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 0;
  }

  return NAV_MOBILE_L2_SLIDE_MS;
};

export const MegaMenuMobile = ({
  isOpen,
  isLoading = false,
  menuItems = [],
  utilityLinks = [],
  navigationMenu,
  onClose,
  onLogoClick,
  onMyAccountClick,
  onMyQuoteClick: _onMyQuoteClick,
  onSearchClick: _onSearchClick,
  primaryTabs = [],
  ...props
}) => {
  const intl = useIntl();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isL2Entered, setIsL2Entered] = useState(false);
  const [activeTabId, setActiveTabId] = useState(primaryTabs[0]?.id || null);
  const [viewportHeight, setViewportHeight] = useState('100vh');
  const throttleTimeoutRef = useRef(null);
  const lastHeightRef = useRef(null);
  const savedScrollYRef = useRef(null);
  const l2EnterFrameRef = useRef(null);
  const l2ExitTimerRef = useRef(null);

  const styles = useMultiStyleConfig('MegaMenu', {isMobile: true});
  const {closeNavDrawer, navDrawerTabId, openNavDrawer, setMegaMenuOpen} = useHeaderContext();
  const isSearchTab = navDrawerTabId === SEARCH_TAB_ID;

  const openSearchTab = useCallback(() => {
    setSelectedCategory(null);
    setIsL2Entered(false);

    if (navDrawerTabId === SEARCH_TAB_ID) {
      return;
    }

    openNavDrawer(SEARCH_TAB_ID);
  }, [navDrawerTabId, openNavDrawer]);

  useEffect(() => {
    setMegaMenuOpen(isOpen);

    if (!isOpen) {
      setSelectedCategory(null);
      setIsL2Entered(false);
      setActiveTabId(primaryTabs[0]?.id || null);
    }
  }, [isOpen, primaryTabs, setMegaMenuOpen]);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;

    savedScrollYRef.current = window.scrollY ?? document.documentElement.scrollTop ?? 0;
    lockBodyScrollFreeze(savedScrollYRef.current);

    return () => {
      unlockBodyScrollFreeze();

      if (typeof savedScrollYRef.current === 'number') {
        window.scrollTo(0, savedScrollYRef.current);
        savedScrollYRef.current = null;
      }
    };
  }, [isOpen]);

  // Get actual viewport height (handles Safari's dynamic address bar)
  const getViewportHeight = useCallback(() => {
    if (typeof window === 'undefined') return '100vh';

    return window.visualViewport ?
      `${window.visualViewport.height}px` :
      `${window.innerHeight}px`;
  }, []);

  // Handle Safari's dynamic address bar - calculate actual viewport height
  // Throttled to prevent excessive re-renders
  const updateViewportHeight = useCallback(() => {
    // Clear any pending throttle timeout
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
    }

    // Throttle updates to max once per 100ms to prevent excessive re-renders
    throttleTimeoutRef.current = setTimeout(() => {
      const newHeight = getViewportHeight();

      // Only update state if height actually changed (prevents unnecessary re-renders)
      if (lastHeightRef.current !== newHeight) {
        lastHeightRef.current = newHeight;
        setViewportHeight(newHeight);
      }
    }, 100);
  }, [getViewportHeight]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    // Set initial height immediately (no throttle for initial render)
    const initialHeight = getViewportHeight();

    lastHeightRef.current = initialHeight;
    setViewportHeight(initialHeight);

    // Update on resize (address bar show/hide triggers resize)
    const eventOptions = {passive: true};

    window.addEventListener('resize', updateViewportHeight, eventOptions);
    window.addEventListener('orientationchange', updateViewportHeight, eventOptions);

    // Visual Viewport API is more accurate for Safari's dynamic address bar
    let visualViewportCleanup;

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight, eventOptions);

      visualViewportCleanup = () => {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      };
    }

    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }

      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);

      if (visualViewportCleanup) {
        visualViewportCleanup();
      }
    };
  }, [isOpen, updateViewportHeight, getViewportHeight]);

  const cancelL2Motion = useCallback(() => {
    if (l2EnterFrameRef.current) {
      cancelAnimationFrame(l2EnterFrameRef.current);
      l2EnterFrameRef.current = null;
    }

    if (l2ExitTimerRef.current) {
      clearTimeout(l2ExitTimerRef.current);
      l2ExitTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelL2Motion();
    };
  }, [cancelL2Motion]);

  const handleArrowClick = (menuItem) => {
    cancelL2Motion();
    setSelectedCategory(menuItem);
    setIsL2Entered(false);

    if (getMobileL2SlideMs() === 0) {
      setIsL2Entered(true);

      return;
    }

    l2EnterFrameRef.current = requestAnimationFrame(() => {
      l2EnterFrameRef.current = requestAnimationFrame(() => {
        l2EnterFrameRef.current = null;
        setIsL2Entered(true);
      });
    });
  };

  const handleSubcategoryBack = () => {
    cancelL2Motion();
    setIsL2Entered(false);

    const slideMs = getMobileL2SlideMs();

    if (slideMs === 0) {
      setSelectedCategory(null);

      return;
    }

    l2ExitTimerRef.current = setTimeout(() => {
      l2ExitTimerRef.current = null;
      setSelectedCategory(null);
    }, slideMs);
  };

  const handleClose = () => {
    cancelL2Motion();
    setSelectedCategory(null);
    setIsL2Entered(false);
    closeNavDrawer();
    onClose();
  };

  const activeTab = primaryTabs.find((tab) => tab.id === activeTabId) || primaryTabs[0];
  const panelMenuItems = activeTab?.menuItems?.length ? activeTab.menuItems : menuItems;
  const promotionalLinks = activeTab?.promotionalLinks?.length ?
    activeTab.promotionalLinks :
    getPromotionalLinks(navigationMenu);

  const isSubcategoryPanelOpen = !!selectedCategory;

  return (
    <Drawer
      blockScrollOnMount={false}
      data-testid="mega-menu-drawer"
      isOpen={isOpen}
      placement="left"
      size="full"
      onClose={handleClose}
      {...props}
    >
      <DrawerOverlay {...styles.mobileDrawerOverlay} zIndex={1098} />
      <DrawerContent
        className="mega-menu-drawer-content"
        display="flex"
        flexDirection="column"
        maxW="100vw"
        w="100%"
        zIndex={1099}
        {...styles.mobileDrawerContent}
        sx={{
          height: viewportHeight,
          maxHeight: viewportHeight,
          ...styles.mobileDrawerContent?.sx,
        }}
      >
        <DrawerHeader
          className="mega-menu-drawer-header"
          data-level={isSubcategoryPanelOpen ? '2' : '1'}
          {...styles.mobileDrawerHeader}
          {...(isSubcategoryPanelOpen ? styles.mobileDrawerHeaderL2 : {})}
        >
          {isSubcategoryPanelOpen ? (
            <IconButton
              aria-label={intl.formatMessage({
                defaultMessage: 'Back',
                id: 'mega_menu.back_button',
              })}
              data-testid="mega-menu-subcategory-panel-back-button"
              icon={<LeftArrowIcon boxSize="12px" {...styles.backButtonIcon} />}
              variant="unstyled"
              onClick={handleSubcategoryBack}
              {...styles.closeButton}
              color="brandColors.primaryText"
            />
          ) : (
            <Box aria-hidden flexShrink={0} h="32px" w="32px" />
          )}
          {isSubcategoryPanelOpen ? (
            <Text as="p" {...styles.mobileDrawerTitle}>
              {selectedCategory.title}
            </Text>
          ) : (
            <IconButton
              className="mega-menu-drawer-logo-button"
              icon={<BrandLogo className="mega-menu-drawer-logo" {...styles.logoMobile} />}
              variant="unstyled"
              onClick={onLogoClick || handleClose}
              {...styles.logoMobileWrapper}
              {...styles.mobileDrawerLogoButton}
            />
          )}
          <IconButton
            aria-label={intl.formatMessage({
              defaultMessage: 'Close navigation',
              id: 'header.nav.close',
            })}
            icon={<HeaderCloseIcon boxSize="20px" />}
            variant="unstyled"
            onClick={handleClose}
            {...styles.closeButton}
          />
        </DrawerHeader>
        <DrawerBody className="mega-menu-drawer-body" {...styles.mobileDrawerBody}>
          {isLoading && !isSearchTab ? (
            <Center flex={1} height="100%" width="100%">
              <Spinner color={styles.megaMenuLoadingSpinner?.color} size="md" />
            </Center>
          ) : (
            <Box height="100%" position="relative" width="100%" {...styles.mobileDrawerBodyInner}>
              <Box
                aria-hidden={isL2Entered}
                className="mega-menu-mobile__l1"
                pointerEvents={isL2Entered ? 'none' : 'auto'}
                transform={isL2Entered ? 'translateX(-100%)' : 'translateX(0)'}
                zIndex={isL2Entered ? 1 : 2}
                {...(isL2Entered ? {inert: ''} : {})}
                {...styles.mobileL1Slide}
              >
                <Search
                  inNavDrawer
                  tabIndex={isL2Entered ? -1 : undefined}
                  onActivate={openSearchTab}
                  onCloseModal={handleClose}
                />
                {!isSearchTab && primaryTabs.length > 0 && (
                  <Box className="mega-menu-mobile__tabs" {...styles.mobileTabsRow}>
                    <NavigationPrimaryTabs
                      activeTabId={activeTabId}
                      tabs={primaryTabs}
                      tone="drawer"
                      onTabClick={(tabId) => {
                        setSelectedCategory(null);
                        setIsL2Entered(false);
                        setActiveTabId(tabId);
                      }}
                    />
                  </Box>
                )}
                {!isSearchTab && (
                  <MegaMenuPanel
                    menuItems={panelMenuItems}
                    promotionalLinks={promotionalLinks}
                    showFunctionalSections
                    transform="none"
                    utilityLinks={utilityLinks}
                    zIndex={1}
                    onArrowClick={handleArrowClick}
                    onMyAccountClick={onMyAccountClick}
                    onSignInNavigate={handleClose}
                    {...styles.mobileL1Panel}
                  />
                )}
              </Box>
              {selectedCategory && (
                <MegaMenuSubcategoryPanel
                  category={selectedCategory}
                  transform={isL2Entered ? 'translateX(0)' : 'translateX(100%)'}
                  zIndex={3}
                />
              )}
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

MegaMenuMobile.displayName = 'MegaMenuMobile';
MegaMenuMobile.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  menuItems: PropTypes.array,
  utilityLinks: PropTypes.array,
  navigationMenu: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onLogoClick: PropTypes.func,
  onMyAccountClick: PropTypes.func,
  onMyQuoteClick: PropTypes.func,
  onSearchClick: PropTypes.func,
  primaryTabs: PropTypes.array,
};
